import { useRef, FocusEvent, RefObject, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { Theme } from '@silvertours/front-shared';
import {
  SearchSuggestion,
  useKeyNavigation,
} from '@silvertours/front-entities';
import { StyledButton, SuggestionWrapper } from './SelectStation.styles';
import { StationItem } from './StationItem';

type Props = {
  connectedInputContainer?: RefObject<HTMLInputElement | null>;
  results: Array<SearchSuggestion> | null;
  onLastElementBlur: (event?: FocusEvent<HTMLElement, Element>) => void;
  onItemClick: (suggestion?: SearchSuggestion) => void;
  onItemSelect: (id?: string) => void;
};

const Suggestion = ({
  connectedInputContainer,
  onLastElementBlur,
  onItemClick,
  onItemSelect,
  results,
}: Props) => {
  const t = useTranslations('features.stage.search.selectDestination');
  const listRef = useRef<HTMLUListElement>(null);
  const isMobileTouchDevice = Theme.useIsMobileTouchDevice();

  useEffect(() => {
    if (isMobileTouchDevice && connectedInputContainer?.current) {
      connectedInputContainer.current.scrollIntoView();
    }
  }, [isMobileTouchDevice, connectedInputContainer]);

  const handleEnter = (item?: SearchSuggestion) => {
    onItemClick(item);
  };

  const handleEscape = () => {
    onLastElementBlur();
  };

  const { focusedItem, onMouseEnter, onMouseLeave } =
    useKeyNavigation<SearchSuggestion>(
      results || [],
      listRef,
      handleEnter,
      handleEscape,
    );

  useEffect(() => {
    onItemSelect(
      focusedItem === null ? undefined : `search-history-item-${focusedItem}`,
    );
  }, [focusedItem, onItemSelect]);

  if (!Array.isArray(results) || !results?.length) {
    return null;
  }

  return (
    <SuggestionWrapper
      ref={listRef}
      id="locationSuggest"
      role="grid"
      aria-label={t('results')}
    >
      {results.map((suggestion, index) => (
        <li key={suggestion.objectID} role="presentation">
          <StyledButton
            type="button"
            tabIndex={0}
            onBlur={onLastElementBlur}
            onMouseDown={() => onItemClick(suggestion)}
            onMouseEnter={() => onMouseEnter(index)}
            onMouseLeave={onMouseLeave}
            id={`locationSuggestItem-${index + 1}`}
            role="row"
            aria-label={`${suggestion.cityName} ${suggestion.typeDisplay}`}
            aria-selected={index === focusedItem}
          >
            <StationItem suggestion={suggestion} />
          </StyledButton>
        </li>
      ))}
    </SuggestionWrapper>
  );
};

export { Suggestion };
